import { ActiveInpatient, PatientHandoverItem } from '../types';

export interface PatientArrivalInfo {
  isArrived: boolean;
  badgeText: string;
  detail: string;
  statusClass: 'arrived' | 'pending';
}

export function formatArrivalDateTime(str: string): string {
  if (!str || !str.trim()) return '';
  const clean = str.trim();

  try {
    let d: Date | null = null;
    if (clean.includes('-')) {
      const parts = clean.split(' ');
      const datePart = parts[0];
      const timePart = parts[1] || '';
      const dateSegments = datePart.split('-');
      if (dateSegments.length === 3) {
        if (dateSegments[0].length === 4) {
          // YYYY-MM-DD
          d = new Date(`${dateSegments[0]}-${dateSegments[1]}-${dateSegments[2]}T${timePart || '00:00:00'}`);
        } else if (dateSegments[2].length === 4) {
          // DD-MM-YYYY
          d = new Date(`${dateSegments[2]}-${dateSegments[1]}-${dateSegments[0]}T${timePart || '00:00:00'}`);
        }
      }
    }

    if (!d || isNaN(d.getTime())) {
      d = new Date(clean);
    }

    if (!isNaN(d.getTime())) {
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = d.toDateString() === yesterday.toDateString();

      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (isToday) {
        return `Today at ${timeStr}`;
      }
      if (isYesterday) {
        return `Yesterday at ${timeStr}`;
      }

      const day = d.getDate();
      const month = d.toLocaleDateString([], { month: 'short' });
      return `${day} ${month}, ${timeStr}`;
    }
  } catch {}

  return clean;
}

export function getPatientArrivalStatus(
  patient: PatientHandoverItem | ActiveInpatient
): PatientArrivalInfo {
  // 1. Explicit arrivalStatus (e.g. arrivalStatus=1)
  const arrivalStatusVal = (patient as any).arrivalStatus;
  if (
    arrivalStatusVal === 1 ||
    arrivalStatusVal === '1' ||
    arrivalStatusVal === true
  ) {
    const rawDate = (patient.doa || (patient as ActiveInpatient).effectiveDateOfAdmission || '') as string;
    return {
      isArrived: true,
      badgeText: 'Arrived (Status: 1)',
      detail: rawDate ? formatArrivalDateTime(rawDate) : 'Arrived (arrivalStatus: 1)',
      statusClass: 'arrived',
    };
  }

  if (
    arrivalStatusVal === 0 ||
    arrivalStatusVal === '0' ||
    arrivalStatusVal === false ||
    arrivalStatusVal === 2 ||
    arrivalStatusVal === '2'
  ) {
    return {
      isArrived: false,
      badgeText: 'Pending Arrival',
      detail: 'Awaiting patient arrival (Status 0)',
      statusClass: 'pending',
    };
  }

  // 2. Explicit boolean or indicator in data
  if (
    patient.isArrived === true ||
    patient.isArrived === 1 ||
    patient.isArrived === '1' ||
    patient.isArrived === 'true'
  ) {
    return {
      isArrived: true,
      badgeText: 'Arrived',
      detail: 'Arrived at ward',
      statusClass: 'arrived',
    };
  }

  if (
    patient.isArrived === false ||
    patient.isArrived === 0 ||
    patient.isArrived === '0' ||
    patient.isArrived === 'false'
  ) {
    return {
      isArrived: false,
      badgeText: 'Pending Arrival',
      detail: 'Awaiting patient arrival',
      statusClass: 'pending',
    };
  }

  // 2. Evaluate Date of Admission (DOA) / effectiveDateOfAdmission
  const rawDate = (
    patient.doa ||
    (patient as ActiveInpatient).effectiveDateOfAdmission ||
    (patient as any).arrivalDate ||
    (patient as any).admittedDate ||
    ''
  ) as string;

  const bed = (
    (patient as ActiveInpatient).admittedBedNumber ||
    patient.bedNumber ||
    ''
  ) as string;

  if (rawDate && rawDate.trim() && rawDate.trim() !== 'null' && rawDate.trim() !== 'undefined') {
    const formatted = formatArrivalDateTime(rawDate.trim());
    return {
      isArrived: true,
      badgeText: 'Arrived',
      detail: formatted,
      statusClass: 'arrived',
    };
  }

  // 3. Evaluate bed assignment
  if (
    bed &&
    bed.trim() &&
    bed.trim() !== 'null' &&
    bed.trim() !== 'undefined' &&
    bed.trim().toUpperCase() !== 'N/A'
  ) {
    return {
      isArrived: true,
      badgeText: 'Arrived',
      detail: `Assigned Bed ${bed.trim()}`,
      statusClass: 'arrived',
    };
  }

  return {
    isArrived: false,
    badgeText: 'Pending Arrival',
    detail: 'Awaiting ward check-in',
    statusClass: 'pending',
  };
}

export type PatientGender = 'male' | 'female' | 'unknown';

export function getPatientGender(patient: any): PatientGender {
  if (!patient) return 'unknown';

  const raw = String(
    patient.gender ||
    patient.genderName ||
    patient.sex ||
    patient.Gender ||
    patient.genderDesc ||
    patient.sexDesc ||
    patient.patientGender ||
    ''
  ).trim().toLowerCase();

  if (raw === 'm' || raw === 'male' || raw === '1' || raw.startsWith('male')) {
    return 'male';
  }
  if (raw === 'f' || raw === 'female' || raw === '2' || raw.startsWith('female')) {
    return 'female';
  }

  // Also check if prefix in name indicates gender (e.g. MR. vs MS. / MRS. / SMT.)
  const name = String(patient.patientName || patient.displayName || patient.firstName || '').trim().toUpperCase();
  if (name.startsWith('MR.') || name.startsWith('MR ') || name.startsWith('MASTER')) {
    return 'male';
  }
  if (
    name.startsWith('MS.') ||
    name.startsWith('MS ') ||
    name.startsWith('MRS.') ||
    name.startsWith('MRS ') ||
    name.startsWith('SMT.') ||
    name.startsWith('SMT ') ||
    name.startsWith('BABY OF')
  ) {
    return 'female';
  }

  return 'unknown';
}

/**
 * Returns consistent SVG avatar markup for a patient's gender
 */
export function renderPatientGenderIcon(gender: PatientGender, size = 28): string {
  if (gender === 'female') {
    return `<img src="/female_icon.svg" class="patient-gender-img" alt="Female" width="${size}" height="${size}" loading="lazy" />`;
  }
  return `<img src="/male_icon.svg" class="patient-gender-img" alt="Male" width="${size}" height="${size}" loading="lazy" />`;
}

/**
 * Resolves patient display name consistently across all components
 */
export function getPatientDisplayName(patient?: any, defaultName = 'Patient'): string {
  if (!patient) return defaultName;
  const name = (
    patient.patientName ||
    patient.displayName ||
    patient.firstName ||
    defaultName
  );
  return String(name).trim();
}

/**
 * Strips duplicate prefixes and returns a clean doctor name with Dr. prefix
 */
export function formatDoctorName(doctor?: string | null): string {
  if (!doctor || !doctor.trim()) return '';
  const clean = doctor.trim().replace(/^dr\.?\s*/i, '');
  return `Dr. ${clean}`;
}

/**
 * Resolves gender by checking direct fields or falling back to inpatient dataset match
 */
export function resolvePatientGender(
  patient: any,
  fallbackPatientsList?: any[]
): PatientGender {
  const direct = getPatientGender(patient);
  if (direct !== 'unknown') return direct;

  const hosp = String(patient?.hospitalNumber || '');
  const ip = String(patient?.ipNumber || '');
  if (hosp && ip && fallbackPatientsList && fallbackPatientsList.length > 0) {
    const match = fallbackPatientsList.find(
      (p) => String(p.hospitalNumber) === hosp && String(p.ipNumber) === ip
    );
    if (match) {
      const matchGender = getPatientGender(match);
      if (matchGender !== 'unknown') return matchGender;
    }
  }

  return 'unknown';
}

